import { Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Field as ChoiceField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldGroup,
  FieldLabel,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  cn,
} from '@column/ui';

import { AddressFields, FormFieldProps, NotificationList, RenderFields } from '~/components';
import { SectionField } from '~/components/RenderFields';
import { useDebounce, useInstitution } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { COUNTERPARTY_ACCOUNT_TYPE_OPTIONS, coerceCounterpartyAccountType } from '~/lib/counterparties/accountType';
import { CounterpartyRepository, CreateCounterparty, FinancialInstitution } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { validateBIC, validateRoutingNumber } from '~/util';

interface CounterpartyForm extends CreateCounterparty {}

type Field = SectionField<CounterpartyForm, undefined>;
type Option = { label: string; value: string };

const ROUTING_NUMBER_TYPE_OPTIONS: Option[] = [
  { label: 'ABA', value: 'aba' },
  { label: 'BIC', value: 'bic' },
];

const BENEFICIARY_TYPE_OPTIONS: Option[] = [
  { label: 'Business', value: 'business' },
  { label: 'Nonprofit', value: 'non_profit' },
  { label: 'Individual', value: 'individual' },
  { label: 'Sole proprietor', value: 'sole_proprietor' },
];

type FieldOptions = Pick<FormFieldProps<CounterpartyForm>, 'rules' | 'hide'>;

const textField = (
  id: Path<CounterpartyForm>,
  label: string,
  placeholder: string,
  extra: FieldOptions = {}
): Field => ({
  id,
  label,
  ...extra,
  children: ({ value, onChange, onBlur }, { error }) => (
    <Input
      value={String(value ?? '')}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      aria-invalid={!!error}
    />
  ),
});

const selectField = (
  id: Path<CounterpartyForm>,
  label: string,
  options: Option[],
  placeholder: string,
  extra: FieldOptions = {}
): Field => ({
  id,
  label,
  ...extra,
  children: ({ value, onChange }, { error }) => (
    <Select value={value ? String(value) : undefined} onValueChange={onChange}>
      <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ),
});

/** Side-by-side outlined radio options; the first option is selected when the value is empty. */
const radioField = (id: Path<CounterpartyForm>, label: string, options: Option[]): Field => ({
  id,
  label,
  children: ({ value, onChange }) => (
    <RadioGroup value={value ? String(value) : options[0].value} onValueChange={onChange}>
      {/* Input height, without FormField's nudge for described options, so it lines up with the other rows. */}
      <FieldGroup variant="outline" orientation="horizontal" className="translate-y-0! [&>[data-slot=field]]:py-0">
        {options.map((option) => {
          const optionId = `counterparty-${id.replace(/\./g, '-')}-${option.value}`;
          return (
            <ChoiceField key={option.value} orientation="horizontal">
              <RadioGroupItem value={option.value} id={optionId} />
              <FieldLabel htmlFor={optionId}>{option.label}</FieldLabel>
            </ChoiceField>
          );
        })}
      </FieldGroup>
    </RadioGroup>
  ),
});

const NOT_FOUND_MESSAGE = 'No financial institution found for this routing number.';
const ROUTING_NUMBER_NOT_FOUND_CODE = 'routing_number_not_found';

type InstitutionLookup =
  | { status: 'idle' | 'loading' | 'not-found'; routingNumber: string }
  | { status: 'found'; routingNumber: string; institution: FinancialInstitution };

/** Looks up the financial institution for a routing number or BIC as it's typed. */
const useInstitutionLookup = (onFound: (institution: FinancialInstitution) => void) => {
  const { createRequest: fetchInstitution } = useInstitution();
  const [lookup, setLookup] = useState<InstitutionLookup>({ status: 'idle', routingNumber: '' });
  // Read by the routing number's validate rule, which can run before a re-render.
  const lookupRef = useRef(lookup);
  const update = (next: InstitutionLookup) => {
    lookupRef.current = next;
    setLookup(next);
  };

  const [fetchDebounced] = useDebounce(
    async (routingNumber: string) => {
      const { institution, notFound } = await fetchInstitution({ routingNumber }).then(
        (result) => ({ institution: result, notFound: false }),
        (error: unknown) => ({
          institution: undefined,
          notFound: (error as { code?: unknown } | undefined)?.code === ROUTING_NUMBER_NOT_FOUND_CODE,
        })
      );
      // A slower response for an earlier value mustn't replace the current one's.
      if (lookupRef.current.routingNumber !== routingNumber) {
        return;
      }
      if (!institution) {
        update({ status: notFound ? 'not-found' : 'idle', routingNumber });
        return;
      }
      update({ status: 'found', routingNumber, institution });
      onFound(institution);
    },
    300,
    []
  );

  const check = (routingNumber: string) => {
    if (!validateRoutingNumber(routingNumber) && !validateBIC(routingNumber)) {
      update({ status: 'idle', routingNumber });
      return;
    }
    update({ status: 'loading', routingNumber });
    fetchDebounced(routingNumber);
  };

  const reset = () => update({ status: 'idle', routingNumber: '' });

  return { lookup, lookupRef, check, reset };
};

const RoutingNumberLookupInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  placeholder: string;
  lookup: InstitutionLookup;
  invalid?: boolean;
}> = ({ value, onChange, onBlur, placeholder, lookup, invalid }) => {
  const isCurrent = lookup.routingNumber === value;
  const institution = isCurrent && lookup.status === 'found' ? lookup.institution : undefined;
  const notFound = isCurrent && lookup.status === 'not-found';
  const message = institution ? (institution.fullName ?? institution.shortName) : notFound ? NOT_FOUND_MESSAGE : null;

  return (
    <>
      <InputGroup className="h-10">
        <InputGroupInput
          value={value}
          onChange={(event) => onChange(event.target.value.trim().toUpperCase())}
          onBlur={onBlur}
          placeholder={placeholder}
          autoComplete="off"
          aria-invalid={invalid || notFound}
        />
        <InputGroupAddon align="inline-end">{isCurrent && lookup.status === 'loading' && <Spinner />}</InputGroupAddon>
      </InputGroup>
      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200',
          message ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <p
            className={cn(
              'mt-1.5 flex items-center gap-1 truncate text-xs leading-5 font-medium',
              institution ? 'text-seafoam-600' : 'text-destructive'
            )}
          >
            {institution && <Icon.CircleCheck className="shrink-0 [--icon-size-x:16px] [--icon-size-y:16px]" />}
            {message}
          </p>
        </div>
      </div>
    </>
  );
};

export interface ModalCounterpartyProps {
  isOpen: boolean;
  onClose: () => void;
  callback?: (counterparty: any) => void;
  type?: string;
}

export const ModalCounterparty: React.FC<ModalCounterpartyProps> = ({ isOpen, onClose, callback, type: modalType }) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const [type, setType] = useState<string>(modalType || '');

  const formMethods = useForm<CounterpartyForm>({
    defaultValues: {
      description: '',
      routingNumber: '',
      routingNumberType: 'aba',
      accountNumber: '',
      accountType: 'checking',
      wireDrawdownAllowed: false,
      wire: {
        beneficiaryName: '',
        beneficiaryEmail: '',
        beneficiaryPhone: '',
        beneficiaryLegalId: '',
        beneficiaryType: undefined,
        localBankCode: '',
        localAccountNumber: '',
        beneficiaryAddress: {
          line1: '',
          line2: '',
          city: '',
          countryCode: '',
          postalCode: '',
          state: '',
        },
      },
    },
  });

  const { handleSubmit, reset, setValue, watch } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();
  const institutionLookup = useInstitutionLookup((institution) => {
    if (institution.routingNumberType) {
      setValue('routingNumberType', institution.routingNumberType);
    }
  });

  const onSubmit = (data: CounterpartyForm) => {
    const payload = { ...data, accountType: coerceCounterpartyAccountType(data.accountType) ?? 'checking' };
    if (type !== 'wire' && type !== 'realtime') {
      delete payload.wire;
    }

    CounterpartyRepository.create(payload)
      .then((response) => {
        if (callback) {
          callback(response);
        }
        onClose();
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  useEffect(() => {
    if (isOpen) {
      reset();
      institutionLookup.reset();
      setType(modalType || '');
    }
  }, [isOpen, reset, modalType]);

  const isWire = type === 'wire';
  const hasBeneficiary = isWire || type === 'realtime';
  const isBic = watch('routingNumberType') === 'bic';

  const fields: Field[] = [
    textField('description', 'Description', 'Description'),
    {
      id: 'routingNumber',
      label: 'Routing Number',
      rules: {
        required: true,
        validate: (v) => {
          const value = String(v ?? '');
          if (isBic ? !validateBIC(value) : !validateRoutingNumber(value)) {
            return isBic ? 'Enter a valid 8 or 11 character BIC.' : 'Enter a valid 9-digit ABA routing number.';
          }
          const { status, routingNumber } = institutionLookup.lookupRef.current;
          return status === 'not-found' && routingNumber === value ? NOT_FOUND_MESSAGE : true;
        },
      },
      children: ({ value, onChange, onBlur }, { error }) => (
        <RoutingNumberLookupInput
          value={String(value ?? '')}
          onChange={(next) => {
            onChange(next);
            institutionLookup.check(next);
          }}
          onBlur={onBlur}
          placeholder={isBic ? 'BIC' : 'Routing number'}
          lookup={institutionLookup.lookup}
          invalid={!!error}
        />
      ),
    },
    radioField('routingNumberType', 'Routing Number Type', ROUTING_NUMBER_TYPE_OPTIONS),
    textField('accountNumber', 'Account Number', 'Account number', { rules: { required: true } }),
    radioField('accountType', 'Account Type', COUNTERPARTY_ACCOUNT_TYPE_OPTIONS),
    textField('wire.beneficiaryName', 'Beneficiary Name', 'Beneficiary name', { hide: !hasBeneficiary }),
    {
      id: 'wireDrawdownAllowed',
      label: 'Wire Drawdown Allowed',
      hide: !isWire,
      children: ({ value, onChange }) => (
        <div className="flex @tablet/form-field:mt-3">
          <Switch aria-label="Wire Drawdown Allowed" checked={Boolean(value)} onCheckedChange={onChange} />
        </div>
      ),
    },
    textField('wire.beneficiaryEmail', 'Beneficiary Email', 'Beneficiary email', { hide: !isWire }),
    textField('wire.beneficiaryPhone', 'Beneficiary Phone', 'Beneficiary phone', { hide: !isWire }),
    textField('wire.beneficiaryLegalId', 'Beneficiary Legal ID', 'Beneficiary legal ID', { hide: !isWire }),
    selectField('wire.beneficiaryType', 'Beneficiary Type', BENEFICIARY_TYPE_OPTIONS, 'Select type', {
      hide: !isWire,
    }),
    textField('wire.localBankCode', 'Local Bank Code', 'Local bank code', { hide: !isWire }),
    textField('wire.localAccountNumber', 'Local Account Number', 'Local account number', { hide: !isWire }),
  ];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* gap-0: the form section's own 24px padding spaces it from the header rule and the footer. */}
      <DialogContent overlayScroll className={cn('gap-0', isWire ? 'sm:max-w-3xl' : 'sm:max-w-xl')}>
        <DialogHeader className="-mx-6 -mt-2 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <DialogTitle>Create Counterparty</DialogTitle>
        </DialogHeader>

        {/* Inside the dialog so errors stack above its overlay. */}
        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit, handleSubmitErrors)} className="-mx-6 [--page-margin:24px]">
            {RenderFields<CounterpartyForm>({ sections: [{ fields }] })}
            {isWire && <AddressFields name="wire.beneficiaryAddress" heading="Beneficiary Address" />}
          </form>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => handleSubmit(onSubmit, handleSubmitErrors)()}>
            <Plus />
            Create &amp; Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
